/**
 * The single network seam of the SPA (ARCHITECTURE §5, §12.5).
 *
 * Every resource module in `src/api` goes through `apiJson`/`apiVoid`/`apiFetch`, so cookie mode,
 * query-string encoding and the uniform `{error:{code,message,details?}}` envelope are handled in
 * exactly one place. Failures surface as a typed `ApiError`; a `401` from any endpoint (other than
 * the boot probe and the login call, which own their own handling) routes the app to /login.
 */

import type { ApiErrorBody, ApiErrorCode } from '@lw/shared';

/** All API paths are relative — the SPA is served from the same origin as the API (§14). */
export const API_BASE = '/api';

/** Server-defined codes plus the two failures that never reach the server. */
export type ApiErrorClientCode = ApiErrorCode | 'network_error' | 'unexpected_response';

export type QueryValue =
  | string
  | number
  | boolean
  | readonly (string | number)[]
  | null
  | undefined;

/** Query params in DTO terms; arrays are CSV-joined per §5.2, booleans become `1`/`0`. */
export type QueryParams = Record<string, QueryValue>;

export type ApiRequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Serialized as a JSON body; `undefined` sends no body. */
  body?: unknown;
  query?: QueryParams;
  signal?: AbortSignal;
  /** Set on `/auth/me` and `/auth/login`, whose 401s are handled by the caller. */
  skipAuthRedirect?: boolean;
};

export type ApiErrorInit = {
  status: number;
  code: ApiErrorClientCode;
  message: string;
  details?: unknown;
  retryAfterSeconds?: number | null;
  cause?: unknown;
};

/** Typed transport failure. `status === 0` means the request never reached the backend. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorClientCode;
  readonly details: unknown;
  /** From the `Retry-After` header or `details.retryAfterSeconds` on a 429 (§10.2). */
  readonly retryAfterSeconds: number | null;

  constructor(init: ApiErrorInit) {
    super(init.message, init.cause === undefined ? undefined : { cause: init.cause });
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.details = init.details;
    this.retryAfterSeconds = init.retryAfterSeconds ?? null;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

export function isUnauthorizedError(error: unknown): boolean {
  return isApiError(error) && error.status === 401;
}

export function isNotFoundError(error: unknown): boolean {
  return isApiError(error) && error.status === 404;
}

/** Message for inline error blocks — never invents detail the server did not send. */
export function describeError(error: unknown): string {
  if (isApiError(error)) return error.message;
  if (error instanceof Error && error.message) return error.message;
  return 'Something went wrong.';
}

/* --------------------------------------------------------- 401 routing hook */

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

/** Registered once by `App` so `client.ts` never imports the router (no cycle). */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

/* ------------------------------------------------------------------ helpers */

export function apiUrl(path: string, query?: QueryParams): string {
  return `${API_BASE}${path}${buildQuery(query)}`;
}

export function buildQuery(params?: QueryParams): string {
  if (!params) return '';
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'boolean') {
      search.set(key, value ? '1' : '0');
    } else if (typeof value === 'string') {
      if (value.length === 0) continue;
      search.set(key, value);
    } else if (typeof value === 'number') {
      search.set(key, String(value));
    } else {
      if (value.length === 0) continue;
      search.set(key, value.join(','));
    }
  }
  const serialized = search.toString();
  return serialized.length > 0 ? `?${serialized}` : '';
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  if (typeof value !== 'object' || value === null || !('error' in value)) return false;
  const inner = (value as { error: unknown }).error;
  return (
    typeof inner === 'object' &&
    inner !== null &&
    typeof (inner as { code?: unknown }).code === 'string' &&
    typeof (inner as { message?: unknown }).message === 'string'
  );
}

function readRetryAfter(response: Response, details: unknown): number | null {
  const header = response.headers.get('Retry-After');
  if (header !== null) {
    const seconds = Number.parseInt(header, 10);
    if (Number.isFinite(seconds)) return seconds;
  }
  if (typeof details === 'object' && details !== null) {
    const fromBody = (details as { retryAfterSeconds?: unknown }).retryAfterSeconds;
    if (typeof fromBody === 'number' && Number.isFinite(fromBody)) return fromBody;
  }
  return null;
}

async function toApiError(response: Response): Promise<ApiError> {
  let payload: unknown;
  try {
    payload = await response.json();
  } catch {
    payload = undefined;
  }
  if (isApiErrorBody(payload)) {
    const { code, message, details } = payload.error;
    return new ApiError({
      status: response.status,
      code,
      message,
      details,
      retryAfterSeconds: readRetryAfter(response, details),
    });
  }
  return new ApiError({
    status: response.status,
    code: 'unexpected_response',
    message: `Request failed (HTTP ${response.status}).`,
    details: payload,
    retryAfterSeconds: readRetryAfter(response, undefined),
  });
}

/* ------------------------------------------------------------------ request */

/** Low-level call: resolves with the raw `Response` on 2xx, throws `ApiError` otherwise. */
export async function apiFetch(path: string, options: ApiRequestOptions = {}): Promise<Response> {
  const { method = 'GET', body, query, signal, skipAuthRedirect = false } = options;

  const headers: Record<string, string> = { Accept: 'application/json' };
  let payload: string | undefined;
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(apiUrl(path, query), {
      method,
      headers,
      body: payload,
      // Session cookie auth (§10.1); the SPA and the API share an origin.
      credentials: 'same-origin',
      signal,
    });
  } catch (cause) {
    if (signal?.aborted) throw cause;
    throw new ApiError({
      status: 0,
      code: 'network_error',
      message: 'The server did not respond.',
      cause,
    });
  }

  if (!response.ok) {
    const error = await toApiError(response);
    if (error.status === 401 && !skipAuthRedirect) unauthorizedHandler?.();
    throw error;
  }
  return response;
}

/**
 * The one non-JSON request body in the app (#90, the content ZIP upload).
 *
 * A deliberate sibling of `apiFetch` rather than a flag on it: a `FormData` body must go out
 * **without** a `Content-Type` header so the browser can append the multipart boundary, and a
 * request that sets one is rejected by the parser with no useful error. Everything else —
 * cookie mode, the error envelope, the 401 hook — is `apiFetch`'s, reached by handing it no body
 * and overriding `body` on the underlying `fetch` here.
 */
export async function apiUpload<T>(
  path: string,
  form: FormData,
  options: { query?: QueryParams; signal?: AbortSignal } = {},
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(apiUrl(path, options.query), {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: form,
      credentials: 'same-origin',
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    if (options.signal?.aborted) throw cause;
    throw new ApiError({
      status: 0,
      code: 'network_error',
      message: 'The server did not respond.',
      cause,
    });
  }

  if (!response.ok) {
    const error = await toApiError(response);
    if (error.status === 401) unauthorizedHandler?.();
    throw error;
  }

  try {
    return (await response.json()) as T;
  } catch (cause) {
    throw new ApiError({
      status: response.status,
      code: 'unexpected_response',
      message: 'The server returned a malformed response.',
      cause,
    });
  }
}

/** Call returning a JSON body. */
export async function apiJson<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
  const response = await apiFetch(path, options);
  try {
    return (await response.json()) as T;
  } catch (cause) {
    throw new ApiError({
      status: response.status,
      code: 'unexpected_response',
      message: 'The server returned a malformed response.',
      cause,
    });
  }
}

/** Call returning `204 No Content`. */
export async function apiVoid(path: string, options: ApiRequestOptions = {}): Promise<void> {
  await apiFetch(path, options);
}
